import {
    CalculationInfo,
    CalculationType,
    CostLine,
    LineCalculationInfo,
    Money,
    OpportunityLineItem
} from 'sr-types/lib/opportunity/v1/graphql';
import {
    CALC_TYPE_TO_ATTRIBUTE,
    DiscountCalculationTypesEnum,
    discountLevelTypes,
    findCalculationTypeByDurationUnit
} from '../common/components/money/costLineAdapter';
import useCostLineAdapter from '../common/components/money/useCostLineAdapter';
import { Listener } from '../common/form/FormContext';
import { getLeaseDuration, getPricingAttributesFromLine } from '../supply/booking/Editor/BookingUtils';

import { GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, GridCellParams } from '@mui/x-data-grid-premium';
import { fromPairs, isEmpty, isNil } from 'lodash';
import { CostSummary } from 'sr-types/lib/search/v1/graphql';
import { SECURITY_ACTIONS, useSecurity } from '../common/auth/Security';
import PricingLine from '../common/components/money/PricingLine';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import { DateRange } from '../common/utils/dateTime';
import { formatMoney, isMoney, moneyToBigNumber, numberToMoney } from '../common/utils/money';
import { NumberParser } from '../common/utils/numbers';
import { computeDurationForDateRange } from '../scheduler/scheduler';
import { GroupType } from '../supply/product/product';
import { PRICING_CONSTANTS } from './PricingConstants';

const financeColumns = ['rate', 'rateType', 'quantity', 'baseTotal', 'itemTotal'];
const discountColumns = ['discountedTotal', 'discountAmount', 'discountRate'];

export const useUpdatePricingAttributes = async (
    selectedLineItemValue,
    attributes,
    activeOrganizationAccount,
    defaultDiscount,
    pricing,
    discountLevel
) => {
    const { getEmptyAdjustments, autoPopulateAdjustments } = useCostLineAdapter();

    if (pricing.lineCalculationInfo?.calculationInfo) {
        pricing.lineCalculationInfo.calculationInfo.attributes = await getPricingAttributesFromLine(
            selectedLineItemValue?.attributes || attributes,
            pricing.lineCalculationInfo.calculationInfo.attributes || [],
            activeOrganizationAccount
        );
        pricing.lineCalculationInfo.calculationInfo.associatedFieldDefinitionReferences =
            pricing.lineCalculationInfo.calculationInfo.attributes.map((attr) => {
                return attr.fieldDefinitionReference;
            });
        if (!pricing.adjustments?.length) {
            pricing.adjustments =
                discountLevel === discountLevelTypes.Booking && autoPopulateAdjustments(defaultDiscount)
                    ? getEmptyAdjustments(defaultDiscount)
                    : [];
        }
        if (
            (!pricing.lineCalculationInfo.calculationInfo.leaseDuration ||
                !pricing.lineCalculationInfo.calculationInfo.leaseDuration.value) &&
            selectedLineItemValue?.dateRange
        ) {
            const scheduleDateRange = selectedLineItemValue.dateRange;
            const leaseDurationVal = getLeaseDuration(scheduleDateRange);
            pricing.lineCalculationInfo.calculationInfo.leaseDuration = {
                unit: scheduleDateRange?.unit,
                value: leaseDurationVal
            };
        }
    }
};

export const preprocessLineItemCost = (line, values) => {
    if (line.groupType === GroupType.Group || line.groupType === GroupType.Item) {
        line.costLine = values.pricing;
        if (!isEmpty(line.costLine.adjustments)) {
            line.costLine.adjustments = line.costLine.adjustments.map((adjustment: LineCalculationInfo) => {
                if (adjustment.calculationInfo.calculationType.code === DiscountCalculationTypesEnum.flt) {
                    delete adjustment.calculationInfo.itemPercent;
                }
                return adjustment;
            });
        }
        line.costLine.rate = values.rates;
        line.costLine.rateName = values.rates?.label || '';

        line.costLine.lineCalculationInfo.calculationInfo.attributes = [
            line.costLine.lineCalculationInfo.calculationInfo.attributes?.find(
                (attr) =>
                    attr.name ===
                    CALC_TYPE_TO_ATTRIBUTE[line.costLine.lineCalculationInfo.calculationInfo.calculationType.code]
            )
        ].filter(Boolean);
        line.costLine.lineCalculationInfo.calculationInfo.associatedFieldDefinitionReferences =
            line.costLine.lineCalculationInfo.calculationInfo.associatedFieldDefinitionReferences?.filter((fd) =>
                line.costLine.lineCalculationInfo.calculationInfo.attributes
                    .map((a) => a.fieldDefinitionReference.id)
                    .includes(fd.id)
            );
    }
};

export const getPricingListeners = (
    prop: string,
    calculationTypes: CalculationType[],
    selectedLineItemId: string
): Listener[] => {
    return [
        {
            prop: `${prop}.dateTimeDuration.value`,
            func: (name, newValue, newState) => {
                const calculationInfo = newState.pricing?.lineCalculationInfo?.calculationInfo;
                const selectedLineItem = newState.lineItems?.find((l) => l.identity.id === selectedLineItemId);
                if (canUpdateLeaseInfo(calculationInfo, selectedLineItem)) {
                    newState.pricing.lineCalculationInfo.calculationInfo.leaseDuration.value = newValue;
                }
            }
        },
        {
            prop: `${prop}.dateTimeDuration.unit`,
            func: (name, newValue, newState) => {
                newState.pricing.lineCalculationInfo.calculationInfo.leaseDuration.unit = newValue;
                const type: CalculationType = findCalculationTypeByDurationUnit(calculationTypes, newValue);
                newState.pricing.lineCalculationInfo.calculationInfo.calculationType = type;
            }
        },
        {
            prop: `currency`,
            func: (name, newValue, newState) => {
                newState.pricing.lineCurrency = newValue;
                if (newState.pricing.lineCalculationInfo.calculationInfo.itemPrice) {
                    newState.pricing.lineCalculationInfo.calculationInfo.itemPrice.currencyCode = newValue;
                }
                if (newState.pricing.lineCalculationInfo.calculationInfo.baseTotal) {
                    newState.pricing.lineCalculationInfo.calculationInfo.baseTotal.currencyCode = newValue;
                }
            }
        }
    ];
};

export const currencyFormatter = (currency, locale) => (value) => {
    if (!value) {
        return value;
    }
    return formatMoney(numberToMoney(value, currency), { showCurrencySymbol: true, locale });
};

export const measurementFormatter = (fieldName) => (value, row) => {
    if (!value) {
        return value;
    }
    const unit = row?.[`${fieldName}Unit`] || '';
    return `${value} ${unit}`;
};

export const percentageFormatter = (value: number): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '';
    }

    const roundedValue = Math.round(value * 100) / 100;
    return `${roundedValue}%`;
};

export const getItemPercent = (adjustments: LineCalculationInfo[]): number =>
    adjustments?.find((adjustment) => adjustment?.adjustmentCategory === PRICING_CONSTANTS.adjustmentCategoryDiscount)
        ?.calculationInfo?.itemPercent;

export const returnValidPercentValue = (itemPercent: number, value: number): number =>
    typeof itemPercent === 'number' && Number.isFinite(itemPercent) && itemPercent !== 0
        ? itemPercent
        : roundedPercentValue(value);

export const roundedPercentValue = (value: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;

export const getBaseTotalValue = (costSummary: CostSummary): number =>
    isMoney(costSummary?.base) ? moneyToBigNumber(costSummary?.base).toNumber() : 0;

export const getDiscountTotalValue = (costSummary: CostSummary): number =>
    isMoney(costSummary?.discount) ? moneyToBigNumber(costSummary?.discount).toNumber() : 0;
export const getCellClassName = () => (params: GridCellParams) => {
    if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
        return 'costAggregationRow';
    }
    return '';
};

export const canUpdateLeaseInfo = (
    calculationInfo: CalculationInfo | undefined,
    selectedLineItem: OpportunityLineItem | undefined
) => {
    return (
        !calculationInfo?.calculationType?.code ||
        !calculationInfo?.leaseDuration?.value ||
        (calculationInfo?.leaseDuration?.value &&
            calculationInfo?.leaseDuration?.unit === selectedLineItem?.dateRange?.dateTimeDuration?.unit)
    );
};

export function getValueFromCostSummary(costSummary, value) {
    return costSummary && isMoney(costSummary[value])
        ? emptyIfZero(moneyToBigNumber(costSummary[value]).toNumber())
        : '';
}
export function emptyIfZero(value) {
    return value === 0 ? '' : value;
}

export const usePricingColumnVisibility = (colDefs) => {
    const { allowed: canReadOrders } = useSecurity(SECURITY_ACTIONS.READ, {
        featureName: FEATURE_CONSTANTS.orders
    });
    const { allowed: canReadDiscounts } = useSecurity(SECURITY_ACTIONS.READ, {
        featureName: FEATURE_CONSTANTS.discounts
    });
    return fromPairs(
        colDefs.map((key) => [
            key.field,
            (financeColumns.includes(key.field) && canReadOrders) ||
                (discountColumns.includes(key.field) && canReadDiscounts && canReadOrders) ||
                (!financeColumns.includes(key.field) && !discountColumns.includes(key.field))
        ])
    );
};

export const calculateLeaseDays = (lineItem: OpportunityLineItem, locale: string): number => {
    let leaseDays = 0;
    const { dateRange, calendar } = lineItem || {};
    const { dateTimeDuration } = dateRange || {};
    if (dateTimeDuration?.unit && dateTimeDuration?.value) {
        const dateRange: DateRange = {
            start: lineItem.dateRange.start,
            end: lineItem.dateRange.end,
            dateTimeDuration: {
                unit: 'day',
                value: dateTimeDuration.value
            },
            duration: lineItem.dateRange.duration,
            startTime: lineItem.dateRange.startTime,
            endTime: lineItem.dateRange.endTime
        };
        leaseDays = computeDurationForDateRange(dateRange, calendar?.id, locale)?.dateTimeDuration?.value || 0;
    }
    return leaseDays;
};

export const resetPricing = (pricing: CostLine): CostLine => ({
    ...pricing,
    lineCalculationInfo: {
        ...pricing?.lineCalculationInfo,
        calculationInfo: {
            ...pricing?.lineCalculationInfo?.calculationInfo,
            itemPrice: undefined,
            calculationType: {
                code: undefined,
                assignTo: 'baseTotal',
                formula: undefined,
                label: undefined
            },
            baseTotal: undefined
        }
    },
    lineTotal: undefined,
    costSummary: {
        discountedTotal: {},
        discount: {},
        base: {},
        total: {},
        taxes: {}
    },
    adjustments:
        pricing?.adjustments?.map((adj) => ({
            ...adj,
            calculationInfo: {
                ...adj.calculationInfo,
                baseTotal: undefined
            }
        })) || []
});

export type RateFields = {
    itemPrice?: Money | null;
    calculationTypeCode?: string | null;
    id?: string;
};

// Reusable helper to safely extract a numeric attribute value using NumberParser
export const extractAttributeNumericValue = (attrValue: string, parser: NumberParser): number => {
    if (!attrValue) return 0;
    try {
        const parsed = JSON.parse(attrValue);
        const parsedValue = parsed?.value;
        if (isNil(parsedValue) || String(parsedValue).trim() === '') return 0;
        const parsedNumber = parser.parse(String(parsedValue));
        return Number.isFinite(parsedNumber) ? parsedNumber : 0;
    } catch (error) {
        return 0;
    }
};

export const getAreaValueForCalculationType = (
    row: PricingLine,
    calculationType: string,
    localeValue: string
): number => {
    const areaAttributeName = CALC_TYPE_TO_ATTRIBUTE[calculationType];
    // Only remap area for calc types backed by area attributes.
    if (!areaAttributeName) {
        return Number(row?.area || 0);
    }
    const matchingAttribute = row?.attributes?.find((attribute) => attribute.name === areaAttributeName);
    if (!matchingAttribute?.value) {
        return 0;
    }
    return extractAttributeNumericValue(matchingAttribute.value, new NumberParser(localeValue));
};
